import { FC, memo, useState, useEffect, useRef } from 'react'
import { CustomIcon } from './customIcon/customIcon'
import customMultiSelectClasses from '../styles/customMultiSelect.module.scss'
import { OutClick } from './outClick'
import cn from 'classnames'
import { Checkbox } from './checkbox/checkbox'

const ITEM_HEIGHT = 47
const MAX_HEIGHT = 428 + 9 + 4

export const CustomMultiSelect: FC<{
  items: string[]
  selectedItems: string[]
  localeText?: string
  selectAllText?: string
  itemLabels?: Record<string, string>
  handleChange: (options: string[]) => void
  classes?: {
    outClickClassName?: string
    selectorWrapperClassName?: string
    selectListItemsClassName?: string
    selectItem?: string
  }
}> = memo(({ items, selectedItems, handleChange, classes, localeText, selectAllText, itemLabels }) => {
  const [open, setOpen] = useState(false)
  const [selectAll, setSelectAll] = useState(false)
  const [scrollPosition, setScrollPosition] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  const handleCloseOpen = () => {
    setOpen((p) => !p)
  }

  const handleClickItem = (item: string) => {
    handleChange(selectedItems.includes(item) ? selectedItems.filter((i) => i !== item) : [...selectedItems, item])
  }

  const handleSelectAll = () => {
    if (selectAll) {
      handleChange([])
      setSelectAll(false)
    } else {
      handleChange(items)
      setSelectAll(true)
    }
  }

  useEffect(() => {
    setSelectAll(items.length > 0 && selectedItems.length === items.length)
  }, [selectedItems, items])

  useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = scrollPosition
    }
  }, [open, scrollPosition])

  const handleScroll = () => {
    if (listRef.current) {
      setScrollPosition(listRef.current.scrollTop)
    }
  }

  return (
    <OutClick
      className={cn(customMultiSelectClasses.outClickWrapper, classes?.outClickClassName && classes.outClickClassName)}
      onClick={open ? handleCloseOpen : () => null}
    >
      <>
        <div
          className={cn(customMultiSelectClasses.customSelectWrapper, classes?.selectorWrapperClassName)}
          onClick={items.length > 1 ? handleCloseOpen : () => {}}
        >
          <div className={customMultiSelectClasses.customSelectActive}>
            {selectedItems.length > 0 ? (
              <span className={customMultiSelectClasses.multiSelectPreview}>
                {selectAll ? selectAllText : selectedItems.map((item) => itemLabels?.[item] ?? item).join(', ')}
              </span>
            ) : (
              <span style={{ color: '#808a9a' }}>{localeText}</span>
            )}
            <CustomIcon icon="arrowRight" />
          </div>
        </div>
        {open && (
          <div
            ref={listRef}
            onScroll={handleScroll}
            style={{ maxHeight: Math.min((items.length + 1) * ITEM_HEIGHT, MAX_HEIGHT) }}
            className={cn(
              customMultiSelectClasses.customSelectListItems,
              classes?.selectListItemsClassName && classes?.selectListItemsClassName,
              open && customMultiSelectClasses.customSelectorOpened
            )}
          >
            <div
              className={cn(customMultiSelectClasses.customMultiSelectItem, customMultiSelectClasses.selectAllItem)}
              onClick={handleSelectAll}
            >
              <span>Выбрать все</span>
              <Checkbox isChecked={selectAll} handleCheckboxChange={() => {}} />
            </div>
            {items.map((item, index) => (
              <div
                key={item + index}
                className={cn(customMultiSelectClasses.customMultiSelectItem)}
                onClick={() => handleClickItem(item)}
              >
                <span>{itemLabels?.[item] ?? item}</span>
                <Checkbox isChecked={selectAll || selectedItems.includes(item)} handleCheckboxChange={() => {}} />
              </div>
            ))}
          </div>
        )}
      </>
    </OutClick>
  )
})
